import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    briefcase: ['M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16', 'M2 21h20', 'M8 7h8'],
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    alertTriangle: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
};

const TABS = [
    { key: 'categories', label: 'Vendor Categories', iconKey: 'briefcase', api: '/categories', description: 'Manage categories to classify your business vendors.' },
];

function ItemModal({ tab, initial, onClose, onSave }) {
    const isEdit = !!initial;
    const [form, setForm] = useState(initial || { name: '', description: '', active: true, isActive: true });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try { await onSave(form); }
        finally { setSaving(false); }
    };

    const singular = tab.label.endsWith('ies') ? tab.label.slice(0, -3) + 'y' :
        tab.label.endsWith('s') ? tab.label.slice(0, -1) : tab.label;

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title">{isEdit ? `Edit ${singular}` : `Add ${singular}`}</h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <div className="md-modal-body">
                    <div className="md-form-group">
                        <label className="md-label">Name<span className="md-required">*</span></label>
                        <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Enter name..." />
                    </div>
                    <div className="md-form-group">
                        <label className="md-label">Description</label>
                        <textarea className="md-textarea" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Enter description..." />
                    </div>
                    <div className="md-form-toggle-row">
                        <label className="md-toggle-label">Active Status</label>
                        <button className={`md-toggle ${form.active && form.isActive ? 'md-toggle-on' : ''}`} onClick={() => setForm({ ...form, active: !form.active, isActive: !form.isActive })}>
                            <span className="md-toggle-thumb" />
                        </button>
                    </div>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-save-btn" onClick={handleSave} disabled={saving || !form.name.trim()} style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', borderRadius: 10 }}>
                        {saving ? 'Saving...' : (isEdit ? 'Update' : `Add ${singular}`)}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DeleteModal({ itemName, onClose, onConfirm }) {
    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal md-modal-sm" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title" style={{ color: '#dc2626' }}>Confirm Deletion</h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <div className="md-modal-body">
                    <p style={{ color: '#475569', lineHeight: 1.6, margin: 0 }}>
                        Are you sure you want to delete <strong>"{itemName}"</strong>?
                    </p>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-save-btn" style={{ background: '#dc2626' }} onClick={() => { onConfirm(); onClose(); }}>Delete</button>
                </div>
            </div>
        </div>
    );
}

export default function VendorSettings() {
    const [activeTab, setActiveTab] = useState('categories');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);

    useEffect(() => {
        fetchItems();
    }, [activeTab]);

    const fetchItems = async () => {
        setLoading(true);
        const tab = TABS.find(t => t.key === activeTab);
        try {
            const res = await axios.get(`${API_BASE}${tab.api}`);
            if (tab.cat) {
                setItems(res.data.filter(i => i.category === tab.cat));
            } else {
                setItems(res.data);
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async (form) => {
        const tab = TABS.find(t => t.key === activeTab);
        const url = `${API_BASE}${tab.api}${form._id ? `/${form._id}` : ''}`;
        const payload = tab.cat ? { ...form, category: tab.cat } : form;

        if (form._id) await axios.put(url, payload);
        else await axios.post(url, payload);

        setModal(null);
        fetchItems();
    };

    const handleDelete = async () => {
        const tab = TABS.find(t => t.key === activeTab);
        await axios.delete(`${API_BASE}${tab.api}/${deleteModal._id}`);
        setDeleteModal(null);
        fetchItems();
    };

    const filtered = items.filter(i => (i.name || '').toLowerCase().includes(search.toLowerCase()));
    const currentTabObj = TABS.find(t => t.key === activeTab);
    const singularLabel = currentTabObj.label.endsWith('ies') ? currentTabObj.label.slice(0, -3) + 'y' :
        currentTabObj.label.endsWith('s') ? currentTabObj.label.slice(0, -1) : currentTabObj.label;

    return (
        <div className="ts-container">
            <aside className="ts-sidebar">
                <div className="ts-nav-item active" style={{ marginBottom: 24, cursor: 'default', background: 'none', color: '#1e293b', paddingLeft: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.5 }}>Vendor Configuration</span>
                </div>
                {TABS.map(tab => (
                    <div
                        key={tab.key}
                        className={`ts-nav-item ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        <Icon d={icons[tab.iconKey]} size={18} />
                        <span>{tab.label}</span>
                    </div>
                ))}
            </aside>

            <main className="ts-main">
                <header className="ts-header">
                    <div className="ts-title-group">
                        <h1>{currentTabObj.label}</h1>
                        <p>{currentTabObj.description}</p>
                    </div>
                    <div className="ts-controls">
                        <div className="md-search-wrap" style={{ margin: 0 }}>
                            <Icon d={icons.search} size={14} />
                            <input className="md-search" placeholder="Quick search..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <button className="md-add-btn" onClick={() => setModal('add')} style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
                            <Icon d={icons.plus} size={14} /> Add {singularLabel}
                        </button>
                    </div>
                </header>

                <div className="md-panel" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Name</th>
                                    <th className="md-th">Description</th>
                                    <th className="md-th">Status</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={4} className="md-td" style={{ textAlign: 'center', padding: 40 }}>Loading configurations...</td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="md-td" style={{ textAlign: 'center', padding: 60 }}>
                                            <Icon d={icons[currentTabObj.iconKey]} size={48} strokeWidth={1} style={{ opacity: 0.2, marginBottom: 12 }} />
                                            <p style={{ color: '#94a3b8' }}>No {currentTabObj.label} found.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(item => (
                                        <tr key={item._id} className="md-tr">
                                            <td className="md-td">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <div style={{ color: '#6366f1' }}><Icon d={icons[currentTabObj.iconKey]} size={14} /></div>
                                                    <strong style={{ color: '#1e293b' }}>{item.name}</strong>
                                                </div>
                                            </td>
                                            <td className="md-td" style={{ color: '#64748b', fontSize: 13, maxWidth: 400 }}>
                                                {item.description || '—'}
                                            </td>
                                            <td className="md-td">
                                                <span className={`md-status-badge ${item.active !== false && item.isActive !== false ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                                    {item.active !== false && item.isActive !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="md-td md-actions">
                                                <button className="md-icon-btn" onClick={() => setModal(item)} title="Edit"><Icon d={icons.edit} size={14} /></button>
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(item)} title="Delete"><Icon d={icons.trash} size={14} /></button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {modal && <ItemModal tab={currentTabObj} initial={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSave={handleSave} />}
            {deleteModal && <DeleteModal itemName={deleteModal.name} onClose={() => setDeleteModal(null)} onConfirm={handleDelete} />}
        </div>
    );
}
